/* eslint-disable no-nested-ternary */
import React, { FC, InputHTMLAttributes, ReactNode, useRef } from 'react';
import { styled } from 'linaria/react';
import { withTheme } from '@shared/lib/theme';
import { useInputFocus } from '@shared/lib';
import { inputBorderRadiusesPx, inputBorderRadiusesRem, inputSizes } from '@shared/config/components/input';
import { Spinner, Text } from '@shared/ui/atoms';
import { CrossIcon } from '@shared/assets/icons';
import { getInputPaddingsPx, getInputPaddingsRem } from '@shared/lib/components/getInputPaddings';
type SizeValue = keyof typeof inputSizes;
type ResponsiveSize = SizeValue | {
  mobile: SizeValue;
  desktop: SizeValue;
};
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'placeholder' | 'onChange'> {
  size?: ResponsiveSize;
  value: string;
  icon?: ReactNode;
  label: string;
  description?: string;
  button?: ReactNode;
  suffix?: ReactNode;
  error?: string;
  isClearable?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onClear?: () => void;
}
const Input: FC<InputProps> = ({
  size = '72',
  value,
  icon,
  label,
  description,
  button,
  suffix,
  error,
  isClearable = false,
  isLoading = false,
  disabled = false,
  onClear,
  ...rest
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const {
    isFocused,
    inputRef
  } = useInputFocus();
  const isLabelAtTop = isFocused || Boolean(value);
  const isError = Boolean(error);
  const buttonRect = buttonRef.current?.getBoundingClientRect();
  const desktopSize = typeof size === 'object' ? size.desktop || '72' : size || '72';
  const mobileSize = typeof size === 'object' ? size.mobile || desktopSize : size || '72';
  return <InputContainer disabled={disabled} isLoading={isLoading}>
      <InputWrapper>
        <LabelWrapper desktopSize={desktopSize} mobileSize={mobileSize} isAtTop={isLabelAtTop}>
          {icon && <IconWrapper isAtTop={isLabelAtTop}>{icon}</IconWrapper>}
          <Text size={isLabelAtTop ? 'pS' : 'pL'} color={isLabelAtTop ? 'secondary' : 'tertiary'} family="MTSCompact">
            {label}
          </Text>
        </LabelWrapper>
        <StyledInput ref={inputRef} desktopSize={desktopSize} mobileSize={mobileSize} value={isLoading ? '' : value} isError={isError} isClearable={isClearable} buttonWidth={buttonRect?.width || 0} {...rest} />
        {suffix && value && !isLoading && <SuffixWrapper desktopSize={desktopSize} mobileSize={mobileSize}>
            <HiddenValue>{value}</HiddenValue>
            {suffix}
          </SuffixWrapper>}
        {isLoading && <LoadingWrapper desktopSize={desktopSize} mobileSize={mobileSize}>
            <Spinner />
          </LoadingWrapper>}
        {(isClearable || button) && <ButtonsWrapper>
            {isClearable && value && <ClearIconButtonWrapper type="button" onClick={onClear}>
                <CrossIcon />
              </ClearIconButtonWrapper>}
            {button && <ButtonWrapper ref={buttonRef}>{button}</ButtonWrapper>}
          </ButtonsWrapper>}
      </InputWrapper>
      {(description || isError) && <BottomTextWrapper>
          {description && !error && <Text family="MTSCompact" size="pS" weight="medium" color="tertiary">
              {description}
            </Text>}
          {error && <Text family="MTSCompact" size="pS" weight="medium" color="negative">
              {error}
            </Text>}
        </BottomTextWrapper>}
    </InputContainer>;
};
export default Input;
type InputContainerProps = Required<Pick<InputProps, 'disabled' | 'isLoading'>>;
const _exp = /*#__PURE__*/() => ({
  disabled,
  isLoading
}) => disabled || isLoading ? 'none' : 'auto';
const _exp2 = /*#__PURE__*/() => ({
  disabled
}) => disabled ? '0.5' : '1';
const InputContainer = /*#__PURE__*/styled('div')({
  name: "InputContainer",
  class: "i8lu7zd",
  propsAsIs: false,
  vars: {
    "i8lu7zd-0": [_exp()],
    "i8lu7zd-1": [_exp2()]
  }
});
const InputWrapper = /*#__PURE__*/styled('div')({
  name: "InputWrapper",
  class: "i1rxsqq2",
  propsAsIs: false
});
type StyledInputProps = {
  isError: boolean;
  buttonWidth: number;
} & Required<Pick<InputProps, 'isClearable'>> & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp3 = /*#__PURE__*/() => ({
  isClearable,
  buttonWidth,
  desktopSize
}) => getInputPaddingsRem(desktopSize, isClearable, buttonWidth);
const _exp4 = /*#__PURE__*/() => ({
  desktopSize
}) => inputBorderRadiusesRem[desktopSize];
const _exp7 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.family.MTSCompact;
const _exp8 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.weight.medium;
const _exp9 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.size.rem.pL;
const _exp0 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.lineHeight.rem.pL;
const _exp1 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.letterSpacing.rem.pL;
const _exp12 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.stroke.hover;
const _exp13 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.stroke.active;
const _exp17 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.tertiary;
const _exp18 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.primary;
const _exp20 = /*#__PURE__*/() => ({
  isClearable,
  buttonWidth,
  mobileSize
}) => getInputPaddingsPx(mobileSize, isClearable, buttonWidth);
const _exp21 = /*#__PURE__*/() => ({
  mobileSize
}) => inputBorderRadiusesPx[mobileSize];
const _exp22 = /*#__PURE__*/() => ({
  isError,
  theme
}) => isError ? theme.colors.stroke.error : 'transparent';
const _exp23 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.size.px.pL;
const _exp24 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.lineHeight.px.pL;
const _exp25 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.letterSpacing.px.pL;
const StyledInput = withTheme(/*#__PURE__*/styled('input')({
  name: "StyledInput",
  class: "s18zeh3s",
  propsAsIs: false,
  vars: {
    "s18zeh3s-0": [_exp3()],
    "s18zeh3s-1": [_exp4()],
    "s18zeh3s-2": [_exp17()],
    "s18zeh3s-3": [_exp22()],
    "s18zeh3s-4": [_exp7()],
    "s18zeh3s-5": [_exp8()],
    "s18zeh3s-6": [_exp9()],
    "s18zeh3s-7": [_exp0()],
    "s18zeh3s-8": [_exp1()],
    "s18zeh3s-9": [_exp18()],
    "s18zeh3s-10": [_exp12()],
    "s18zeh3s-11": [_exp13()],
    "s18zeh3s-12": [_exp20()],
    "s18zeh3s-13": [_exp21()],
    "s18zeh3s-14": [_exp23()],
    "s18zeh3s-15": [_exp24()],
    "s18zeh3s-16": [_exp25()]
  }
}));
type OverlaySizeProps = {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp26 = /*#__PURE__*/() => ({
  desktopSize
}) => desktopSize === '72' ? '0.75rem' : '0.5rem';
const _exp27 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.family.MTSCompact;
const _exp28 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.weight.medium;
const _exp29 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.size.rem.pL;
const _exp30 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.lineHeight.rem.pL;
const _exp31 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.letterSpacing.rem.pL;
const _exp32 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.text.primary;
const _exp33 = /*#__PURE__*/() => ({
  mobileSize
}) => mobileSize === '72' ? '11px' : '7px';
const _exp34 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.size.px.pL;
const _exp35 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.lineHeight.px.pL;
const _exp36 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.letterSpacing.px.pL;
const SuffixWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "SuffixWrapper",
  class: "s1qy3hyr",
  propsAsIs: false,
  vars: {
    "s1qy3hyr-0": [_exp26()],
    "s1qy3hyr-1": [_exp27()],
    "s1qy3hyr-2": [_exp28()],
    "s1qy3hyr-3": [_exp29()],
    "s1qy3hyr-4": [_exp30()],
    "s1qy3hyr-5": [_exp31()],
    "s1qy3hyr-6": [_exp32()],
    "s1qy3hyr-7": [_exp33()],
    "s1qy3hyr-8": [_exp34()],
    "s1qy3hyr-9": [_exp35()],
    "s1qy3hyr-10": [_exp36()]
  }
}));
const HiddenValue = /*#__PURE__*/styled('span')({
  name: "HiddenValue",
  class: "h1y65vri",
  propsAsIs: false
});
const _exp37 = /*#__PURE__*/() => ({
  desktopSize
}) => desktopSize === '72' ? '0.75rem' : '0.5rem';
const _exp38 = /*#__PURE__*/() => ({
  mobileSize
}) => mobileSize === '72' ? '12px' : '8px';
const LoadingWrapper = /*#__PURE__*/styled('div')({
  name: "LoadingWrapper",
  class: "lyrxc9u",
  propsAsIs: false,
  vars: {
    "lyrxc9u-0": [_exp37()],
    "lyrxc9u-1": [_exp38()]
  }
});
const ButtonsWrapper = /*#__PURE__*/styled('div')({
  name: "ButtonsWrapper",
  class: "b19hxdfq",
  propsAsIs: false
});
const _exp39 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.icon.secondary;
const ClearIconButtonWrapper = withTheme(/*#__PURE__*/styled('button')({
  name: "ClearIconButtonWrapper",
  class: "ccd2q23",
  propsAsIs: false,
  vars: {
    "ccd2q23-0": [_exp39()]
  }
}));
const ButtonWrapper = /*#__PURE__*/styled('div')({
  name: "ButtonWrapper",
  class: "b1taubn2",
  propsAsIs: false
});
type LabelWrapperProps = {
  isAtTop: boolean;
} & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp40 = /*#__PURE__*/() => ({
  desktopSize,
  isAtTop
}) => isAtTop ? desktopSize === '72' ? '0.625rem' : '0.375rem' : '50%';
const _exp41 = /*#__PURE__*/() => ({
  isAtTop
}) => isAtTop ? 'none' : 'translateY(-50%)';
const _exp42 = /*#__PURE__*/() => ({
  isAtTop
}) => isAtTop ? '0.125rem' : '0.25rem';
const _exp43 = /*#__PURE__*/() => ({
  mobileSize,
  isAtTop
}) => isAtTop ? mobileSize === '72' ? '10px' : '6px' : '50%';
const _exp44 = /*#__PURE__*/() => ({
  isAtTop
}) => isAtTop ? '2px' : '4px';
const LabelWrapper = /*#__PURE__*/styled('div')({
  name: "LabelWrapper",
  class: "l1l5kmlk",
  propsAsIs: false,
  vars: {
    "l1l5kmlk-0": [_exp40()],
    "l1l5kmlk-1": [_exp41()],
    "l1l5kmlk-2": [_exp42()],
    "l1l5kmlk-3": [_exp43()],
    "l1l5kmlk-4": [_exp44()]
  }
});
interface IconWrapperProps {
  isAtTop: boolean;
}
const _exp46 = /*#__PURE__*/() => ({
  isAtTop
}) => isAtTop ? '1rem' : '1.5rem';
const _exp48 = /*#__PURE__*/() => ({
  isAtTop,
  theme
}) => isAtTop ? theme.colors.icon.secondary : theme.colors.icon.tertiary;
const _exp50 = /*#__PURE__*/() => ({
  isAtTop
}) => isAtTop ? '16px' : '24px';
const IconWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "iaz66cp",
  propsAsIs: false,
  vars: {
    "iaz66cp-0": [_exp46()],
    "iaz66cp-1": [_exp48()],
    "iaz66cp-2": [_exp50()]
  }
}));
const BottomTextWrapper = /*#__PURE__*/styled('div')({
  name: "BottomTextWrapper",
  class: "b1nadvh4",
  propsAsIs: false
});

require("./Input.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./Input.tsx");